import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Collapse,
    Container,
    Divider,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Progress,
    SimpleGrid,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
    Textarea,
    useToast,
    VStack,
} from '@chakra-ui/react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import Loading from '../compnents/Loading';
import ConfirmDialog from '../compnents/ConfirmDialog';
import { PlanCard, PlanComparisonTable } from '../compnents/PlanCatalog';
import {
    DATA_NEEDED, OFFER, PLANS, SELF_SERVE_CHECKOUT_ENABLED, formatEuro, planForBillingTier,
} from '../config/plans';
import { normalizePurchaseIntent, toSubscriptionPlanKey } from '../utils/purchaseIntent';
import { normalizeOperationalHealth } from '../utils/operationalHealth';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

const SELF_SERVE_PLANS = ['solo', 'team', 'business'];
const PAID_STATUSES = new Set(['active', 'trialing', 'past_due']);

const formatDate = (value) => (value
    ? new Date(value).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
    : '');

const limitText = (value) => (value === null || value === undefined ? 'not limited' : value.toLocaleString('en-US'));

// People / active clients chosen for a configurable plan (Team, Business).
const CapacityPicker = ({ planKey, value, onChange }) => {
    const plan = OFFER.plans[planKey];
    if (!plan.extensions.seats && !plan.extensions.customerBlocks) return null;
    return (
        <SimpleGrid columns={2} spacing={3} mb={2}>
            <FormControl>
                <FormLabel fontSize="xs" mb={1}>People</FormLabel>
                <NumberInput
                    size="sm"
                    min={1}
                    max={plan.max.seats}
                    value={value.seats}
                    onChange={(_, number) => onChange({ ...value, seats: Number.isFinite(number) ? number : plan.included.seats })}
                >
                    <NumberInputField aria-label={`${plan.name} people`} />
                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
            </FormControl>
            <FormControl>
                <FormLabel fontSize="xs" mb={1}>Active clients</FormLabel>
                <NumberInput
                    size="sm"
                    min={1}
                    max={plan.max.customers}
                    step={OFFER.addons.customerBlock.size}
                    value={value.customers}
                    onChange={(_, number) => onChange({ ...value, customers: Number.isFinite(number) ? number : plan.included.customers })}
                >
                    <NumberInputField aria-label={`${plan.name} active clients`} />
                    <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
            </FormControl>
        </SimpleGrid>
    );
};

const QuoteSummary = ({ quote, error }) => {
    if (error) return <Text fontSize="xs" color="red.600">{error}</Text>;
    if (!quote) return null;
    const packageName = quote.planId === 'business_capacity' ? '20-person package' : null;
    return (
        <Box bg="gray.50" borderRadius="md" p={3} fontSize="sm" data-testid={`quote-${quote.requested.planId}`}>
            <Text fontWeight="800">
                {formatEuro(quote.total)} / {quote.cycle === 'yearly' ? 'year' : 'month'} + VAT
            </Text>
            <Text fontSize="xs" color="gray.600">
                {packageName ? `${packageName} · ` : ''}{quote.limits.seats} {quote.limits.seats === 1 ? 'person' : 'people'} · {quote.limits.customers.toLocaleString('en-US')} active clients
            </Text>
            {quote.lineItems.length > 1 ? (
                <List fontSize="xs" color="gray.600" mt={1}>
                    {quote.lineItems.map((item) => (
                        <ListItem key={item.kind}>
                            {item.kind === 'base' ? 'Plan' : item.kind === 'seat' ? 'Extra people' : 'Client blocks'}: {item.quantity} × {formatEuro(item.unitAmount)}
                        </ListItem>
                    ))}
                </List>
            ) : null}
        </Box>
    );
};

function SubscriptionContent() {
    const { token, user, refreshUser, isAuthInitializing } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const purchaseIntent = useMemo(() => normalizePurchaseIntent(location.search), [location.search]);
    const intendedPlan = toSubscriptionPlanKey(purchaseIntent.plan);

    const [cycle, setCycle] = useState(purchaseIntent.billing === 'yearly' ? 'yearly' : 'monthly');
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [busyAction, setBusyAction] = useState(null);
    const [configuration, setConfiguration] = useState(() => Object.fromEntries(
        SELF_SERVE_PLANS.map((key) => [key, { seats: OFFER.plans[key].included.seats, customers: OFFER.plans[key].included.customers }])
    ));
    const [quotes, setQuotes] = useState({});
    const [quoteErrors, setQuoteErrors] = useState({});
    const [change, setChange] = useState(null);
    const [acceptLegacyExit, setAcceptLegacyExit] = useState(false);
    const [operationalHealth, setOperationalHealth] = useState(null);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({ name: '', email: '', phoneNumber: '', useCaseDescription: '' });
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    // The backend says whether Stripe carries the catalogue prices; until then
    // plan cards ask for a proposal instead of offering a checkout.
    const [checkoutAvailable, setCheckoutAvailable] = useState(false);

    const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
    const isAdmin = user?.role === 'admin';

    const notifyError = useCallback((title, error) => {
        toast({
            title,
            description: error?.response?.data?.message || 'Please try again in a moment.',
            status: 'error',
            duration: 6000,
            isClosable: true,
        });
    }, [toast]);

    const loadStatus = useCallback(async () => {
        if (!token || !user) return;
        setLoadingStatus(true);
        try {
            const [status, workspaceUsage] = await Promise.all([
                axios.get(`${API_BASE_URL}/subscription/subscription-status`, authHeaders),
                axios.get(`${API_BASE_URL}/subscription/usage`, authHeaders).catch(() => ({ data: null })),
            ]);
            setSubscriptionData(status.data);
            setUsage(workspaceUsage.data);
            if (status.data?.hasSubscription && status.data.billingCycle) setCycle(status.data.billingCycle);
        } catch (error) {
            console.error('Error fetching subscription status:', error);
        } finally {
            setLoadingStatus(false);
        }
    }, [token, user, authHeaders]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/subscription/catalog`)
            .then(({ data }) => setCheckoutAvailable(Boolean(data?.checkout?.available)))
            .catch(() => setCheckoutAvailable(false));
    }, []);

    useEffect(() => {
        if (!token || !isAdmin) return;
        axios.get(`${API_BASE_URL}/subscription/funnel-delivery-status`, authHeaders)
            .then(({ data }) => setOperationalHealth(normalizeOperationalHealth(data)))
            .catch(() => setOperationalHealth(null));
    }, [token, isAdmin, authHeaders]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('paymentSuccess') && !params.get('paymentCancelled')) return undefined;
        toast({
            title: params.get('paymentSuccess') ? 'Payment received' : 'Checkout cancelled',
            description: params.get('paymentSuccess')
                ? 'Your plan is being activated; it can take a few seconds to appear.'
                : 'Nothing was charged.',
            status: params.get('paymentSuccess') ? 'success' : 'warning',
            duration: 4000,
            isClosable: true,
        });
        const timer = setTimeout(() => navigate('/subscription', { replace: true }), 1100);
        return () => clearTimeout(timer);
    }, [navigate, toast]);

    // Server-side quote for each self-serve plan and the chosen capacity.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            SELF_SERVE_PLANS.forEach((planId) => {
                axios.post(`${API_BASE_URL}/subscription/quote`, { planId, billingCycle: cycle, ...configuration[planId] })
                    .then(({ data }) => {
                        if (cancelled) return;
                        setQuotes((current) => ({ ...current, [planId]: data }));
                        setQuoteErrors((current) => ({ ...current, [planId]: null }));
                    })
                    .catch((error) => {
                        if (cancelled) return;
                        setQuotes((current) => ({ ...current, [planId]: null }));
                        setQuoteErrors((current) => ({ ...current, [planId]: error.response?.data?.message || 'Price unavailable' }));
                    });
            });
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [cycle, configuration]);

    const entitlement = subscriptionData?.entitlement || user?.entitlement || null;
    const hasPaidSubscription = Boolean(subscriptionData?.hasSubscription && PAID_STATUSES.has(subscriptionData?.status) && subscriptionData?.stripeSubscriptionId);
    const hasActiveTrial = subscriptionData?.status === 'trialing' || user?.trialStatus === 'active';
    const trialUsed = subscriptionData?.trial && subscriptionData.trial.status !== 'none';
    const canStartTrial = !hasPaidSubscription && !hasActiveTrial && !trialUsed;
    const currentPlanId = subscriptionData?.planId || entitlement?.id || null;
    const readOnlyUntil = subscriptionData?.readOnlyUntil || user?.readOnlyUntil || null;
    const attention = new Set((operationalHealth?.attention || []).map((item) => item.code));

    const startTrial = async (planId) => {
        setBusyAction(`trial-${planId}`);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/subscription/start-trial`, { planId }, authHeaders);
            toast({ title: data.message, description: 'Upload your client extract to see your first results.', status: 'success', duration: 5000, isClosable: true });
            await refreshUser();
            await loadStatus();
            navigate('/datasets');
        } catch (error) {
            notifyError('Trial unavailable', error);
        } finally {
            setBusyAction(null);
        }
    };

    const buy = async (planId) => {
        setBusyAction(`buy-${planId}`);
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/subscription/create`,
                { planId, billingCycle: cycle, ...configuration[planId] },
                authHeaders,
            );
            window.location.href = data.url;
        } catch (error) {
            const blockers = error.response?.data?.blockers;
            if (blockers?.length) {
                toast({ title: 'Resize the workspace first', description: blockers.map((b) => b.message).join(' '), status: 'warning', duration: 9000, isClosable: true });
            } else {
                notifyError('Checkout unavailable', error);
            }
            setBusyAction(null);
        }
    };

    const previewChange = async (planId) => {
        setBusyAction(`change-${planId}`);
        setAcceptLegacyExit(false);
        try {
            const body = { planId, billingCycle: cycle, ...configuration[planId] };
            const { data } = await axios.post(`${API_BASE_URL}/subscription/preview-change`, body, authHeaders);
            setChange({ body, preview: data });
        } catch (error) {
            if (error.response?.data?.code === 'LEGACY_PLAN_EXIT_REQUIRED') {
                setChange({ body: { planId, billingCycle: cycle, ...configuration[planId] }, legacyExit: error.response.data });
            } else {
                notifyError('Plan change unavailable', error);
            }
        } finally {
            setBusyAction(null);
        }
    };

    const previewLegacyExit = async () => {
        setBusyAction('legacy-preview');
        try {
            const body = { ...change.body, acceptLegacyExit: true };
            const { data } = await axios.post(`${API_BASE_URL}/subscription/preview-change`, body, authHeaders);
            setChange({ body, preview: data });
        } catch (error) {
            notifyError('Plan change unavailable', error);
        } finally {
            setBusyAction(null);
        }
    };

    const confirmChange = async () => {
        setBusyAction('confirm-change');
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/subscription/update`,
                { ...change.body, changeToken: change.preview.changeToken },
                authHeaders,
            );
            toast({
                title: data.direction === 'upgrade' ? 'Plan updated' : 'Change scheduled',
                description: data.direction === 'upgrade'
                    ? 'The new plan is active now.'
                    : `Your current plan stays active until ${formatDate(data.pendingChange?.effectiveAt)}.`,
                status: 'success',
                duration: 6000,
                isClosable: true,
            });
            setChange(null);
            await refreshUser();
            await loadStatus();
        } catch (error) {
            notifyError('Plan change failed', error);
        } finally {
            setBusyAction(null);
        }
    };

    const cancelSubscription = async () => {
        setBusyAction('cancel');
        try {
            await axios.post(`${API_BASE_URL}/subscription/cancel`, {}, authHeaders);
            toast({ title: 'Cancellation scheduled', description: 'Access continues until the end of the paid period.', status: 'info', duration: 5000, isClosable: true });
            await loadStatus();
        } catch (error) {
            notifyError('Cancellation failed', error);
        } finally {
            setBusyAction(null);
            setIsCancelDialogOpen(false);
        }
    };

    const reactivate = async () => {
        setBusyAction('reactivate');
        try {
            await axios.post(`${API_BASE_URL}/subscription/reactivate`, {}, authHeaders);
            toast({ title: 'Subscription continues', status: 'success', duration: 4000, isClosable: true });
            await loadStatus();
        } catch (error) {
            notifyError('Reactivation failed', error);
        } finally {
            setBusyAction(null);
        }
    };

    const retryAnalytics = async () => {
        setBusyAction('retry-analytics');
        try {
            const { data } = await axios.post(`${API_BASE_URL}/subscription/funnel-delivery-retry`, {}, authHeaders);
            setOperationalHealth(normalizeOperationalHealth(data));
        } catch (error) {
            notifyError('Analytics recovery unavailable', error);
        } finally {
            setBusyAction(null);
        }
    };

    const submitContact = async () => {
        if (!contactForm.name || !contactForm.email || !contactForm.phoneNumber || !contactForm.useCaseDescription) {
            toast({ title: 'Missing fields', description: 'Please fill in all fields.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setBusyAction('contact');
        try {
            await axios.post(`${API_BASE_URL}/subscription/custom-subscription-contact-form`, contactForm, authHeaders);
            toast({ title: 'Request sent', description: 'We will reply by email.', status: 'success', duration: 5000, isClosable: true });
            setContactForm({ name: '', email: '', phoneNumber: '', useCaseDescription: '' });
            setShowContactForm(false);
        } catch (error) {
            notifyError('Request not sent', error);
        } finally {
            setBusyAction(null);
        }
    };

    const openContact = (subject) => {
        setContactForm((current) => ({ ...current, useCaseDescription: current.useCaseDescription || subject }));
        setShowContactForm(true);
    };

    if (isAuthInitializing || !token) return <Loading />;
    if (!user) {
        return (
            <Box minH="100vh" display="flex" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    const planActions = (planKey) => {
        if (!isAdmin) return null;
        if (planKey === 'enterprise') {
            return <Button variant="outline" onClick={() => openContact('Enterprise scoping: systems to connect, identity provider, volumes, timing.')}>Scope a project</Button>;
        }
        const plan = OFFER.plans[planKey];
        const quote = quotes[planKey];
        const buttons = [];
        if (hasPaidSubscription) {
            const sameConfiguration = quote && currentPlanId === quote.planId
                && subscriptionData?.billingCycle === cycle
                && entitlement?.limits?.seats === quote.limits.seats
                && entitlement?.limits?.customers === quote.limits.customers;
            buttons.push(
                <Button key="change" colorScheme="teal" isDisabled={!quote || sameConfiguration} isLoading={busyAction === `change-${planKey}`} onClick={() => previewChange(planKey)}>
                    {sameConfiguration ? 'Current plan' : 'Review change'}
                </Button>,
            );
        } else {
            if (canStartTrial && OFFER.trial.selfServePlans.includes(planKey)) {
                buttons.push(
                    <Button key="trial" colorScheme="teal" isLoading={busyAction === `trial-${planKey}`} onClick={() => startTrial(planKey)}>
                        Start 30-day {plan.name} trial
                    </Button>,
                );
            }
            if (canStartTrial && OFFER.trial.onRequestPlans.includes(planKey)) {
                buttons.push(
                    <Button key="trial-request" variant="outline" onClick={() => openContact(`${plan.name} trial request: our case, the data we can export and who reviews.`)}>
                        Request a {plan.name} trial
                    </Button>,
                );
            }
            if (SELF_SERVE_CHECKOUT_ENABLED && checkoutAvailable) {
                buttons.push(
                    <Button key="buy" variant={buttons.length ? 'outline' : 'solid'} colorScheme="teal" isDisabled={!quote} isLoading={busyAction === `buy-${planKey}`} onClick={() => buy(planKey)}>
                        Buy {plan.name}{quote ? ` · ${formatEuro(quote.total)}` : ''}
                    </Button>,
                );
            } else {
                buttons.push(<Button key="proposal" variant="outline" onClick={() => openContact(`${plan.name} proposal`)}>Request a proposal</Button>);
            }
        }
        return (
            <>
                <CapacityPicker planKey={planKey} value={configuration[planKey]} onChange={(value) => setConfiguration((current) => ({ ...current, [planKey]: value }))} />
                <QuoteSummary quote={quote} error={quoteErrors[planKey]} />
                {buttons}
            </>
        );
    };

    const preview = change?.preview;

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8}>
                    <VStack spacing={6} align="stretch" mb={10}>
                        <Box>
                            <Badge colorScheme="teal" borderRadius="full" px={3} py={1} mb={2}>Plan and billing</Badge>
                            <Heading size="lg">
                                {entitlement?.name ? `${entitlement.name} workspace` : 'Choose how you want to work'}
                            </Heading>
                            <Text color="gray.600" maxW="3xl" mt={1}>
                                Solo organises one adviser’s reviews, Team coordinates a firm, Business controls the whole book.
                                Every plan uses the same methodology and shows what your data supports and what is missing.
                            </Text>
                        </Box>

                        {loadingStatus ? <Progress size="xs" isIndeterminate colorScheme="teal" /> : null}

                        {readOnlyUntil ? (
                            <Alert status="warning" borderRadius="lg">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>Your plan has ended — export access until {formatDate(readOnlyUntil)}</AlertTitle>
                                    <AlertDescription>
                                        You can still export or delete the data in this workspace. Uploads, analyses and actions need an active plan.
                                        Nothing is deleted automatically when this period ends; contact us to agree what happens to the data.
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        ) : null}

                        {entitlement?.legacy ? (
                            <Alert status="info" borderRadius="lg">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>{entitlement.name} — plan no longer sold</AlertTitle>
                                    <AlertDescription>
                                        Your terms and access stay exactly as they are. Moving to Solo, Team or Business is optional and ends the {entitlement.name} terms.
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        ) : null}

                        {hasActiveTrial ? (
                            <Alert status="success" borderRadius="lg">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>{planForBillingTier(user.subscription)?.name || 'Trial'} trial — {subscriptionData?.trial?.daysRemaining ?? user.trialDaysRemaining ?? 0} days left</AlertTitle>
                                    <AlertDescription>
                                        No card and no automatic conversion. The trial uses the plan’s real limits. Choose a plan before it ends to keep processing; your data stays exportable for {OFFER.afterEnd.exportWindowDays} days after it.
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        ) : null}

                        {hasPaidSubscription ? (
                            <Alert status={subscriptionData.status === 'past_due' ? 'warning' : 'info'} borderRadius="lg">
                                <AlertIcon />
                                <Box>
                                    <AlertTitle>
                                        {subscriptionData.status === 'past_due'
                                            ? 'Payment issue — Stripe is retrying'
                                            : `${entitlement?.name || 'Subscription'} · ${subscriptionData.billingCycle}`}
                                    </AlertTitle>
                                    <AlertDescription>
                                        {subscriptionData.cancelAtPeriodEnd
                                            ? `Ends on ${formatDate(subscriptionData.currentPeriodEnd)}.`
                                            : `Renews on ${formatDate(subscriptionData.currentPeriodEnd)}.`}
                                        {subscriptionData.pendingChange
                                            ? ` Scheduled change: ${OFFER.plans[subscriptionData.pendingChange.planId]?.name || subscriptionData.pendingChange.planId} from ${formatDate(subscriptionData.pendingChange.effectiveAt)}.`
                                            : ''}
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        ) : null}

                        {intendedPlan && !hasPaidSubscription ? (
                            <Alert status="info" borderRadius="lg">
                                <AlertIcon />
                                <AlertDescription>You selected {PLANS[intendedPlan].name} when you signed up; it is highlighted below.</AlertDescription>
                            </Alert>
                        ) : null}

                        {attention.has('checkout_pending') || attention.has('checkout_recovery_available') ? (
                            <Alert status="info" borderRadius="lg">
                                <AlertIcon />
                                <AlertDescription>
                                    A checkout for {OFFER.plans[operationalHealth?.checkout?.plan]?.name || 'a plan'} was started and not completed. Choose the plan again below to continue; the open session is reused.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {attention.has('analytics_delivery_exhausted') ? (
                            <Alert status="warning" borderRadius="lg">
                                <AlertIcon />
                                <AlertDescription flex="1">Some product analytics events could not be delivered.</AlertDescription>
                                <Button size="sm" onClick={retryAnalytics} isLoading={busyAction === 'retry-analytics'}>Retry delivery</Button>
                            </Alert>
                        ) : null}

                        {usage ? (
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <Stat borderWidth="1px" borderRadius="lg" p={4} bg="white">
                                    <StatLabel>People with access</StatLabel>
                                    <StatNumber>{usage.seats.used} of {limitText(usage.seats.limit)}</StatNumber>
                                    <StatHelpText>{OFFER.definitions.seat}</StatHelpText>
                                </Stat>
                                <Stat borderWidth="1px" borderRadius="lg" p={4} bg="white">
                                    <StatLabel>Active clients</StatLabel>
                                    <StatNumber>
                                        {usage.customers.available ? usage.customers.used.toLocaleString('en-US') : '—'} of {limitText(usage.customers.limit)}
                                    </StatNumber>
                                    <StatHelpText>{OFFER.definitions.activeCustomer}</StatHelpText>
                                </Stat>
                            </SimpleGrid>
                        ) : null}

                        {isAdmin ? (
                            <>
                                <HStack justify="space-between" flexWrap="wrap" pt={2}>
                                    <Heading size="md">Plans</Heading>
                                    <ButtonGroup size="sm" isAttached variant="outline">
                                        <Button onClick={() => setCycle('monthly')} isActive={cycle === 'monthly'} aria-pressed={cycle === 'monthly'}>Monthly</Button>
                                        <Button onClick={() => setCycle('yearly')} isActive={cycle === 'yearly'} aria-pressed={cycle === 'yearly'}>Yearly (10 months)</Button>
                                    </ButtonGroup>
                                </HStack>
                                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                                    {['solo', 'team', 'business', 'enterprise'].map((planKey) => (
                                        <PlanCard
                                            key={planKey}
                                            planKey={planKey}
                                            cycle={cycle}
                                            variant="light"
                                            highlighted={intendedPlan === planKey || currentPlanId === planKey || (planKey === 'business' && currentPlanId === 'business_capacity')}
                                            badge={currentPlanId === planKey || (planKey === 'business' && currentPlanId === 'business_capacity') ? 'Current plan' : null}
                                            actions={planActions(planKey)}
                                        />
                                    ))}
                                </SimpleGrid>
                                <Text fontSize="sm" color="gray.600">{DATA_NEEDED}</Text>
                                <PlanComparisonTable variant="light" />

                                <Box borderWidth="1px" borderRadius="lg" p={5} bg="white">
                                    <HStack justify="space-between" flexWrap="wrap">
                                        <Box>
                                            <Heading size="sm">Optional services and tailored requests</Heading>
                                            <Text fontSize="sm" color="gray.600" mt={1}>
                                                Assisted onboarding: Team {formatEuro(OFFER.services.assistedOnboardingTeam.price)} (up to {OFFER.services.assistedOnboardingTeam.hours} h), Business {formatEuro(OFFER.services.assistedOnboardingBusiness.price)} (up to {OFFER.services.assistedOnboardingBusiness.hours} h).
                                                Diagnostic Flash {formatEuro(OFFER.services.flashDiagnostic.price)} if you want us to analyse a sample of your data first.
                                                Additional agreed work {formatEuro(OFFER.services.additionalWorkHourly.price)}/hour. None is required to subscribe.
                                            </Text>
                                        </Box>
                                        <Button size="sm" variant="outline" onClick={() => setShowContactForm(!showContactForm)}>
                                            {showContactForm ? 'Close' : 'Contact us'}
                                        </Button>
                                    </HStack>
                                    <Collapse in={showContactForm} animateOpacity>
                                        <VStack spacing={3} mt={4} align="stretch">
                                            <FormControl isRequired><FormLabel fontSize="sm">Name</FormLabel><Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} /></FormControl>
                                            <FormControl isRequired><FormLabel fontSize="sm">Email</FormLabel><Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} /></FormControl>
                                            <FormControl isRequired><FormLabel fontSize="sm">Phone</FormLabel><Input value={contactForm.phoneNumber} onChange={(e) => setContactForm({ ...contactForm, phoneNumber: e.target.value })} /></FormControl>
                                            <FormControl isRequired><FormLabel fontSize="sm">What do you need?</FormLabel><Textarea rows={4} value={contactForm.useCaseDescription} onChange={(e) => setContactForm({ ...contactForm, useCaseDescription: e.target.value })} /></FormControl>
                                            <Button colorScheme="teal" alignSelf="flex-start" onClick={submitContact} isLoading={busyAction === 'contact'}>Send request</Button>
                                        </VStack>
                                    </Collapse>
                                </Box>

                                {hasPaidSubscription ? (
                                    <Box borderWidth="1px" borderRadius="lg" p={5} bg={subscriptionData.cancelAtPeriodEnd ? 'brand.tealSoft' : 'white'}>
                                        {subscriptionData.cancelAtPeriodEnd ? (
                                            <HStack justify="space-between" flexWrap="wrap">
                                                <Text>Your subscription ends on {formatDate(subscriptionData.currentPeriodEnd)}.</Text>
                                                <Button colorScheme="teal" onClick={reactivate} isLoading={busyAction === 'reactivate'}>Keep my subscription</Button>
                                            </HStack>
                                        ) : (
                                            <HStack justify="space-between" flexWrap="wrap">
                                                <Text fontSize="sm" color="gray.600">
                                                    Cancelling stops the renewal; access continues until the end of the paid period, then your data stays exportable for {OFFER.afterEnd.exportWindowDays} days.
                                                </Text>
                                                <Button colorScheme="red" variant="outline" onClick={() => setIsCancelDialogOpen(true)}>Cancel subscription</Button>
                                            </HStack>
                                        )}
                                    </Box>
                                ) : null}
                            </>
                        ) : (
                            <Alert status="info" borderRadius="lg">
                                <AlertIcon />
                                <AlertDescription>Your firm’s admin manages the plan and billing.</AlertDescription>
                            </Alert>
                        )}
                    </VStack>
                </Container>
            </Box>
            <Footer />

            <Modal isOpen={Boolean(change)} onClose={() => setChange(null)} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Review the change</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {change?.legacyExit ? (
                            <VStack align="stretch" spacing={3}>
                                <Text>{change.legacyExit.message}</Text>
                                <Checkbox isChecked={acceptLegacyExit} onChange={(e) => setAcceptLegacyExit(e.target.checked)}>
                                    I understand and want to move to the current plans
                                </Checkbox>
                            </VStack>
                        ) : null}
                        {preview ? (
                            <VStack align="stretch" spacing={3}>
                                <Text fontWeight="700">
                                    {OFFER.plans[preview.planId]?.name}: {formatEuro(preview.newRecurringTotal)} / {preview.cycle === 'yearly' ? 'year' : 'month'} + VAT
                                    {' '}({preview.limits.seats} {preview.limits.seats === 1 ? 'person' : 'people'}, {preview.limits.customers.toLocaleString('en-US')} active clients)
                                </Text>
                                {preview.direction === 'upgrade' ? (
                                    <Text>
                                        Applies now. Charged today: <b>{formatEuro(preview.amountDueNow)}</b>
                                        {preview.taxDueNow ? ` (of which tax ${formatEuro(preview.taxDueNow)})` : ' plus applicable tax'}, prorated for the rest of the current period.
                                    </Text>
                                ) : (
                                    <Text>
                                        Applies at the next renewal, on {formatDate(preview.effectiveAt)}. Nothing is charged today and your current plan stays active until then.
                                    </Text>
                                )}
                                {preview.blockers?.length ? (
                                    <Alert status="warning" borderRadius="md">
                                        <AlertIcon />
                                        <List spacing={1} fontSize="sm">
                                            {preview.blockers.map((blocker) => <ListItem key={blocker.code}>{blocker.message}</ListItem>)}
                                        </List>
                                    </Alert>
                                ) : null}
                                <Divider />
                                <Text fontSize="xs" color="gray.500">The quote is valid for 15 minutes.</Text>
                            </VStack>
                        ) : null}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setChange(null)}>Close</Button>
                        {change?.legacyExit && !preview ? (
                            <Button colorScheme="teal" isDisabled={!acceptLegacyExit} isLoading={busyAction === 'legacy-preview'} onClick={previewLegacyExit}>Show the price</Button>
                        ) : (
                            <Button colorScheme="teal" isDisabled={!preview?.changeToken} isLoading={busyAction === 'confirm-change'} onClick={confirmChange}>
                                {preview?.direction === 'upgrade' ? 'Confirm and pay' : 'Schedule change'}
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ConfirmDialog
                isOpen={isCancelDialogOpen}
                onClose={() => setIsCancelDialogOpen(false)}
                onConfirm={cancelSubscription}
                title="Cancel subscription"
                description="The subscription will not renew. Access continues until the end of the paid period; then your data stays exportable for 30 days."
                confirmLabel="Cancel subscription"
                isLoading={busyAction === 'cancel'}
            />
        </Box>
    );
}

export default function Subscription() {
    return <SubscriptionContent />;
}
